import BaseDrawer from "@stardust/base-drawer";
import wishlist from '@stardust/wishlist';

// Event name constants (previously from events.js)
const Events = {
  productSwiper: { open: 'product-swiper:open', close: 'product-swiper:close' },
  keyboard: { esc: 'keydown:escape' },
};

const SWIPE_THRESHOLD = 100;
const ROTATION_FACTOR = 0.15;

class ProductSwiperDrawer extends BaseDrawer {
  constructor(element) {
    super(element, { openClass: 'opacity-0 scale-95', closeClass: 'opacity-0 scale-95', animationDuration: 300 });
    this.products = [];
    this.currentIndex = 0;
    this.swipeHistory = [];
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;

    ['_onTouchStart', '_onTouchMove', '_onTouchEnd', '_onMouseDown', '_onMouseMove', '_onMouseUp'].forEach(fn => this[fn] = this[fn].bind(this));
  }

  load() {
    this.cardContainer = this.element.querySelector('[data-swiper-cards]');
    this.nopeBtn = this.element.querySelector('[data-swiper-nope-btn]');
    this.likeBtn = this.element.querySelector('[data-swiper-like-btn]');
    this.undoBtn = this.element.querySelector('[data-swiper-undo-btn]');
    this._setupEventListeners();
  }

  _setupEventListeners() {
    document.addEventListener(Events.productSwiper.open, e => {
      const { products, collectionHandle } = e.detail || {};
      this._openWithProducts(products, collectionHandle);
    });
    document.addEventListener(Events.productSwiper.close, this._boundClose);
    document.addEventListener(Events.keyboard.esc, () => this.isOpen && this.close());

    this.nopeBtn?.addEventListener('click', () => this._swipe('left'));
    this.likeBtn?.addEventListener('click', () => this._swipe('right'));
    this.undoBtn?.addEventListener('click', () => this._undo());

    document.addEventListener('keydown', e => {
      if (!this.isOpen) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); this._swipe('left'); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); this._swipe('right'); }
      else if (e.key === 'z' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); this._undo(); }
    });
  }

  async _openWithProducts(products, collectionHandle) {
    this.open();
    try {
      this.products = products?.length ? products
        : collectionHandle ? await this._fetchProducts(`/collections/${collectionHandle}/products.json?limit=50`)
          : await this._fetchProducts('/collections/all/products.json?limit=50');
      this.products = this._shuffle([...this.products]);
      this.currentIndex = 0;
      this.swipeHistory = [];
      this._renderCards();
    } catch (e) { console.error('[ProductSwiper]', e); }
  }

  async _fetchProducts(url) {
    const res = await fetch(url);
    if (!res.ok) throw new Error('Failed to fetch');
    return (await res.json()).products || [];
  }

  _shuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  _renderCards() {
    if (!this.cardContainer) return;
    this.cardContainer.innerHTML = '';
    this.products.slice(this.currentIndex, this.currentIndex + 3).reverse().forEach((product, i, arr) => {
      this.cardContainer.appendChild(this._createCard(product, arr.length - 1 - i));
    });
    this._attachSwipeListeners();
    this._updateUndoButton();
  }

  _createCard(product, stackIndex) {
    const card = document.createElement('div');
    card.className = 'product-swiper-card';
    card.dataset.productId = product.id;
    card.dataset.stackIndex = stackIndex;
    card.style.zIndex = 10 - stackIndex;
    card.style.backgroundImage = `url('${this._getImageUrl(product.images?.[0]?.src || product.featured_image || product.image, 1200)}')`;

    const price = this._formatPrice(product.variants?.[0]?.price || product.price);
    const comparePrice = product.variants?.[0]?.compare_at_price || product.compare_at_price;

    card.innerHTML = `
      <div class="product-swiper-card__stamps">
        <div class="product-swiper-card__stamp product-swiper-card__stamp--nope" data-stamp="nope">NOPE</div>
        <div class="product-swiper-card__stamp product-swiper-card__stamp--like" data-stamp="like">LIKE</div>
      </div>
      <div class="product-swiper-card__info">
        <h3 class="text-2xl font-bold mb-1">${this._escapeHtml(product.title)}</h3>
        <div class="flex items-center gap-2 text-lg">
          <span class="font-semibold">${price}</span>
          ${comparePrice ? `<span class="text-white/70 line-through text-base">${this._formatPrice(comparePrice)}</span>` : ''}
        </div>
      </div>`;
    return card;
  }

  _getTopCard() {
    return this.cardContainer?.querySelector('[data-stack-index="0"]');
  }

  _attachSwipeListeners() {
    const card = this._getTopCard();
    if (!card) return;
    card.addEventListener('touchstart', this._onTouchStart, { passive: true });
    card.addEventListener('touchmove', this._onTouchMove, { passive: false });
    card.addEventListener('touchend', this._onTouchEnd);
    card.addEventListener('mousedown', this._onMouseDown);
    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mouseup', this._onMouseUp);
  }

  _onTouchStart(e) { this._startDrag(e.touches[0].clientX); }
  _onTouchMove(e) { if (this.isDragging) { e.preventDefault(); this._updateDrag(e.touches[0].clientX); } }
  _onTouchEnd() { this._endDrag(); }
  _onMouseDown(e) { e.preventDefault(); this._startDrag(e.clientX); }
  _onMouseMove(e) { if (this.isDragging) this._updateDrag(e.clientX); }
  _onMouseUp() { this._endDrag(); }

  _startDrag(x) {
    this.isDragging = true;
    this.startX = x;
    this.currentX = 0;
    const card = this._getTopCard();
    if (card) card.style.transition = 'none';
  }

  _updateDrag(x) {
    this.currentX = x - this.startX;
    const card = this._getTopCard();
    if (!card) return;

    card.style.transform = `translateX(${this.currentX}px) rotate(${this.currentX * ROTATION_FACTOR}deg)`;

    const like = card.querySelector('[data-stamp="like"]');
    const nope = card.querySelector('[data-stamp="nope"]');
    const opacity = Math.min((Math.abs(this.currentX) - 50) / 100, 1);

    like.style.opacity = this.currentX > 50 ? opacity : 0;
    nope.style.opacity = this.currentX < -50 ? opacity : 0;
  }

  _endDrag() {
    if (!this.isDragging) return;
    this.isDragging = false;

    const card = this._getTopCard();
    if (card) card.style.transition = 'transform 0.3s ease';

    if (this.currentX > SWIPE_THRESHOLD) this._swipe('right');
    else if (this.currentX < -SWIPE_THRESHOLD) this._swipe('left');
    else this._resetCardPosition();

    this.currentX = 0;
  }

  _resetCardPosition() {
    const card = this._getTopCard();
    if (!card) return;
    card.style.transform = 'translateX(0) rotate(0deg)';
    card.querySelector('[data-stamp="like"]').style.opacity = 0;
    card.querySelector('[data-stamp="nope"]').style.opacity = 0;
  }

  _swipe(direction) {
    const card = this._getTopCard();
    if (!card || this.currentIndex >= this.products.length) return;

    const product = this.products[this.currentIndex];
    const isRight = direction === 'right';

    card.style.transition = 'transform 0.4s ease-out';
    card.style.transform = `translateX(${isRight ? '' : '-'}${window.innerWidth}px) rotate(${isRight ? 30 : -30}deg)`;
    card.querySelector(`[data-stamp="${isRight ? 'like' : 'nope'}"]`).style.opacity = 1;

    this.swipeHistory.push({ index: this.currentIndex, direction, product });
    if (isRight) this._addToWishlist(product);

    this.currentIndex++;
    setTimeout(() => this.currentIndex >= this.products.length ? this.close() : this._renderCards(), 400);
  }

  _undo() {
    if (!this.swipeHistory.length) return;
    const { index, direction, product } = this.swipeHistory.pop();
    if (direction === 'right') this._removeFromWishlist(product);
    this.currentIndex = index;
    this._renderCards();
  }

  _addToWishlist(product) {
    const card = document.createElement('div');
    card.dataset.productHandle = product.handle;
    card.dataset.productId = product.id;
    wishlist.add(document.createElement('button'), card);
  }

  _removeFromWishlist(product) {
    wishlist.removeByHandle(product.handle);
  }

  _updateUndoButton() {
    if (!this.undoBtn) return;
    const disabled = !this.swipeHistory.length;
    this.undoBtn.disabled = disabled;
    this.undoBtn.classList.toggle('opacity-50', disabled);
  }

  _formatPrice(cents) {
    if (!cents) return '$0.00';
    const amount = typeof cents === 'string' && cents.includes('.') ? parseFloat(cents) : cents / 100;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  }

  _escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  _getImageUrl(url, size) {
    if (!url) return '';
    if (url.includes('cdn.shopify.com') || url.includes('shopify.com/s/files')) {
      return `${url}${url.includes('?') ? '&' : '?'}width=${size}`;
    }
    return url;
  }
}

const init = () => {
  const productSwiperDrawer = document.querySelector('[data-product-swiper-drawer]');
  if (productSwiperDrawer) new ProductSwiperDrawer(productSwiperDrawer).load();
};
init();

export default ProductSwiperDrawer;
